import React, { useState } from 'react';
import PageHero from '../../components/ui-shree/PageHero';
import SectionLabel from '../../components/ui-shree/SectionLabel';
import { submitContact } from '../../lib/api';
import { BRAND } from '../../lib/constants';

const initial = { name: '', email: '', subject: '', message: '' };

const ContactPage = () => {
  const [form, setForm] = useState(initial);
  const [state, setState] = useState({ loading: false, ok: false, err: '' });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, err: '' });
    try {
      await submitContact(form);
      setState({ loading: false, ok: true, err: '' });
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || 'Could not deliver the letter — please try again.';
      setState({
        loading: false,
        ok: false,
        err: typeof detail === 'string' ? detail : 'Please check your entries.',
      });
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Correspondence"
        title={<>Write a <em className="text-[#90B0BF]">letter</em> to the house.</>}
        subtitle="For atelier appointments, press, partnerships, or the simple pleasure of being heard. We answer every letter, slowly."
        testId="contact-hero"
      />

      <section className="px-6 md:px-12 pb-32">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="md:col-span-7 space-y-8"
          >
            <SectionLabel testId="contact-form-label">The Letter</SectionLabel>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <Field label="Your name">
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={update('name')}
                  data-testid="contact-name-input"
                  className="contact-input"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update('email')}
                  data-testid="contact-email-input"
                  className="contact-input"
                />
              </Field>
            </div>

            <Field label="Subject (optional)">
              <input
                type="text"
                value={form.subject}
                onChange={update('subject')}
                data-testid="contact-subject-input"
                className="contact-input"
              />
            </Field>

            <Field label="Message">
              <textarea
                required
                rows={6}
                value={form.message}
                onChange={update('message')}
                data-testid="contact-message-input"
                className="contact-input resize-none"
              />
            </Field>

            <div className="flex items-center gap-6 flex-wrap">
              <button
                type="submit"
                disabled={state.loading}
                data-testid="contact-submit-button"
                className="bg-[#7C9CB1] text-[#F4F8F9] px-10 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors disabled:opacity-50"
              >
                {state.loading ? 'Sending…' : 'Send the letter'}
              </button>

              {state.ok && (
                <p
                  data-testid="contact-success"
                  className="font-serif italic text-[#7C9CB1] text-lg"
                >
                  Received with care. We will write back.
                </p>
              )}
              {state.err && (
                <p
                  data-testid="contact-error"
                  className="text-sm text-[#7C9CB1] font-light"
                >
                  {state.err}
                </p>
              )}
            </div>
          </form>

          {/* Side info */}
          <aside className="md:col-span-5 space-y-10">
            <div>
              <SectionLabel>The Atelier</SectionLabel>
              <p className="font-serif text-2xl text-[#7C9CB1] font-light leading-snug">
                The Atelier of Shreesavaa<br />Heavener, Oklahoma · USA
              </p>
            </div>
            <div>
              <SectionLabel>Direct</SectionLabel>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-direct-email"
                className="font-mono text-sm text-[#7C9CB1] hover:text-[#90B0BF] transition-colors"
              >
                {BRAND.email}
              </a>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#90B0BF] mt-2">
                The official correspondence of the house
              </p>
            </div>
          </aside>
        </div>
      </section>

      <style>{`
        .contact-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(124,156,177,0.4);
          color: #7C9CB1;
          font-family: 'Outfit', sans-serif;
          font-weight: 300;
          padding: 12px 2px;
          font-size: 16px;
          letter-spacing: 0.01em;
        }
        .contact-input:focus {
          outline: none;
          border-bottom-color: #7C9CB1;
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
      {label}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

export default ContactPage;
